import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Gear,
  Clock,
  CurrencyDollar,
  Key,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  Eye,
  EyeSlash,
  ListBullets,
  MapPin,
  CloudArrowUp,
  ShieldCheck,
  ShieldWarning,
  Stack,
  FloppyDisk,
  // Module-card icons
  House,
  Users as UsersIcon,
  UserCircleGear,
  GraduationCap,
  Storefront,
  Wallet,
  CreditCard,
  IdentificationCard,
  ChartLineUp,
  LockKey,
  X as XIcon,
  MagnifyingGlass,
  Building,
  Gauge,
  EnvelopeSimple,
  Sun,
  Target,
  ChatText,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { InstrumentationConfig } from '../components/config/InstrumentationConfig';
import { CoursesConfig } from '../components/config/CoursesConfig';
import { EmailsConfig } from '../components/config/EmailsConfig';
import { DayStarterConfig } from '../components/config/DayStarterConfig';
import { ScheduledJobsConfig } from '../components/config/ScheduledJobsConfig';
import { CxTargetsConfig } from '../components/config/CxTargetsConfig';
import { CxTemplatesConfig } from '../components/config/CxTemplatesConfig';

// API_URL imported from utils

export default function ConfigurationsPage() {
  const { config } = useTenant();
  const { isSuperAdmin, isEntityAdmin } = useAuth();
  
  const [middlewareConfig, setMiddlewareConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);
  // System-level toggles (super admin only)
  const [writesSetting, setWritesSetting] = useState(null);
  const [writesSaving, setWritesSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    middleware_url: '',
    username: '',
    password: '',
    refresh_interval_minutes: 30,
    is_active: false
  });

  const fetchMiddlewareConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/middleware/config`, {
        withCredentials: true,
        headers
      });
      if (data) {
        setMiddlewareConfig(data);
        setFormData({
          middleware_url: data.middleware_url || '',
          username: data.username || '',
          password: '',
          refresh_interval_minutes: data.refresh_interval_minutes || 30,
          is_active: data.is_active || false
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching middleware config:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMiddlewareConfig();
  }, [fetchMiddlewareConfig]);

  // ============== System-level: middleware-writes toggle ==============
  const fetchWritesSetting = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/system/middleware-writes`, { headers, withCredentials: true });
      setWritesSetting(data);
    } catch (e) {
      // Silent — endpoint only exists for super admins.
    }
  }, [isSuperAdmin]);

  useEffect(() => { fetchWritesSetting(); }, [fetchWritesSetting]);

  // ============== Phone-digit rules per country ==============
  // Backend endpoints:
  //   GET  /api/config/country-phone-rules             — read the {defaults, east_africa_default, overrides} shape.
  //   PUT  /api/config/countries/{code}/phone-rules    — super-admin write.
  const [phoneRules, setPhoneRules] = useState(null);
  const fetchPhoneRules = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/config/country-phone-rules`, { headers, withCredentials: true });
      setPhoneRules(data || null);
    } catch (e) { setPhoneRules(null); }
  }, []);
  useEffect(() => { fetchPhoneRules(); }, [fetchPhoneRules]);

  // Resolve the effective rule for one country_code
  const resolvePhoneRule = useCallback((code) => {
    if (!phoneRules) return { min: 7, max: 15, source: 'default', override: false };
    const cc = (code || '').toUpperCase();
    if (phoneRules.overrides && phoneRules.overrides[cc]) {
      return { min: phoneRules.overrides[cc].min_digits, max: phoneRules.overrides[cc].max_digits, source: 'override', override: true };
    }
    const east = new Set(['TZ', 'KE', 'UG', 'RW', 'BI', 'ET', 'SS']);
    if (east.has(cc) && phoneRules.east_africa_default) {
      return { min: phoneRules.east_africa_default.min_digits, max: phoneRules.east_africa_default.max_digits, source: 'east_africa_default', override: false };
    }
    return { min: (phoneRules.defaults || {}).min_digits || 7, max: (phoneRules.defaults || {}).max_digits || 15, source: 'default', override: false };
  }, [phoneRules]);

  const savePhoneRule = useCallback(async (code, min, max) => {
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.put(
      `${API_URL}/api/config/countries/${code}/phone-rules`,
      { min_digits: Number(min), max_digits: Number(max) },
      { headers, withCredentials: true },
    );
    await fetchPhoneRules();
  }, [fetchPhoneRules]);

  const deletePhoneRule = useCallback(async (code) => {
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.delete(
      `${API_URL}/api/config/countries/${code}/phone-rules`,
      { headers, withCredentials: true },
    );
    await fetchPhoneRules();
  }, [fetchPhoneRules]);

  const setWritesEnabled = async (newValue) => {
    setWritesSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API_URL}/api/system/middleware-writes`,
        { enabled: newValue },
        { headers, withCredentials: true },
      );
      toast.success(
        newValue === null
          ? 'Reverted to auto-detect'
          : newValue
            ? 'Middleware writes ENABLED'
            : 'Middleware writes DISABLED',
      );
      await fetchWritesSetting();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the setting');
    } finally {
      setWritesSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    // Validate required fields for new config
    if (!middlewareConfig) {
      if (!formData.middleware_url) {
        toast.error('Middleware URL is required');
        return;
      }
      if (!formData.username) {
        toast.error('Username is required');
        return;
      }
      if (!formData.password) {
        toast.error('Password is required');
        return;
      }
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const payload = { ...formData };
      if (!payload.password) {
        delete payload.password;
      }
      
      const { data } = await axios.post(`${API_URL}/api/middleware/config`, payload, {
        withCredentials: true,
        headers
      });
      
      setMiddlewareConfig(data);
      toast.success('Middleware configuration saved');
    } catch (error) {
      const message = formatApiError(error, 'Failed to save configuration');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshToken = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API_URL}/api/middleware/refresh-token`, {}, {
        withCredentials: true,
        headers
      });
      
      toast.success('Token refreshed successfully');
      fetchMiddlewareConfig();
    } catch (error) {
      const message = formatApiError(error, 'Failed to refresh token');
      toast.error(message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!window.confirm('Are you sure you want to delete the middleware configuration?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${API_URL}/api/middleware/config`, {
        withCredentials: true,
        headers
      });
      
      setMiddlewareConfig(null);
      setFormData({
        middleware_url: '',
        username: '',
        password: '',
        refresh_interval_minutes: 30,
        is_active: false
      });
      toast.success('Middleware configuration deleted');
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  if (!isSuperAdmin && !isEntityAdmin) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-8 text-center">
        <p className="text-[#78716C]">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configurations-page">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-[#1C1917] tracking-tight"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Configurations
        </h1>
        <p 
          className="text-[#78716C] mt-1"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          System settings and preferences
        </p>
      </div>

      {/* Tabs Container */}
      <Tabs defaultValue={isSuperAdmin ? "middleware" : "timezones"} className="w-full">
        <TooltipProvider delayDuration={150}>
        <TabsList className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-1 h-auto inline-flex gap-1 w-auto">
          {isSuperAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="middleware"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid="tab-middleware"
                  aria-label="Middleware API Token"
                >
                  <Key size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>API</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>Middleware API Token</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="timezones"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-timezones"
                aria-label="Timezone"
              >
                <Clock size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Timezone</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>Timezones</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="currencies"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-currencies"
                aria-label="Currency"
              >
                <CurrencyDollar size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Currency</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>Currencies</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="countries"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-countries"
                aria-label="Countries"
              >
                <MapPin size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Countries</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>Countries</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="courses"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-courses"
                aria-label="Courses"
              >
                <GraduationCap size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Courses</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>Student courses per campus</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="cx-targets"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-cx-targets"
                aria-label="CX"
              >
                <Target size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>CX</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>ARU targets & dormancy thresholds</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <TabsTrigger
                value="cx-templates"
                className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                data-testid="tab-cx-templates"
                aria-label="Templates"
              >
                <ChatText size={16} />
                <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Templates</span>
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>SMS & email copy for CX campaigns</TooltipContent>
          </Tooltip>
          {isSuperAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="emails"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid="tab-emails"
                  aria-label="Emails"
                >
                  <EnvelopeSimple size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Emails</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>SMTP email settings & notifications</TooltipContent>
            </Tooltip>
          )}
          {isSuperAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="modules"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid="tab-modules"
                  aria-label="Modules"
                >
                  <Stack size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Modules</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>Modules</TooltipContent>
            </Tooltip>
          )}
          {isSuperAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger
                  value="instrumentation"
                  className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white rounded-lg h-10 px-3 gap-2 text-sm"
                  data-testid="tab-instrumentation"
                  aria-label="Instrumentation"
                >
                  <Gauge size={16} />
                  <span className="font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Instrumentation</span>
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={6}>Instrumentation tools & maintenance schedules</TooltipContent>
            </Tooltip>
          )}
        </TabsList>
        </TooltipProvider>

        {/* Middleware API Token Tab */}
        {isSuperAdmin && (
          <TabsContent value="middleware" className="mt-6">
            <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Key size={20} className="text-[#0A2540]" />
                  <h2 
                    className="text-lg font-semibold text-[#0A2540] tracking-tight"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    Middleware API Token Configuration
                  </h2>
                </div>
                {middlewareConfig && (
                  <div className="flex items-center gap-2">
                    {middlewareConfig.is_active ? (
                      <span className="flex items-center gap-1 text-sm text-[#10B981]">
                        <CheckCircle size={16} weight="fill" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-[#78716C]">
                        <XCircle size={16} />
                        Inactive
                      </span>
                    )}
                  </div>
                )}
              </div>

              <p className="text-sm text-[#78716C] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Configure the middleware API authentication. The system will automatically refresh the token at the specified interval.
              </p>

              {loading ? (
                <div className="animate-pulse space-y-4">
                  <div className="h-10 bg-[#EDE6D8] rounded"></div>
                  <div className="h-10 bg-[#EDE6D8] rounded"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Configuration Form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Middleware URL <span className="text-[#EF4444]">*</span></Label>
                      <Input
                        value={formData.middleware_url}
                        onChange={(e) => setFormData({ ...formData, middleware_url: e.target.value })}
                        placeholder="https://api.example.com/auth"
                        className="border-[#EDE6D8] rounded-lg"
                        data-testid="middleware-url-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Refresh Interval (minutes)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="1440"
                        value={formData.refresh_interval_minutes}
                        onChange={(e) => setFormData({ ...formData, refresh_interval_minutes: parseInt(e.target.value) || 30 })}
                        className="border-[#EDE6D8] rounded-lg"
                        data-testid="refresh-interval-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username <span className="text-[#EF4444]">*</span></Label>
                      <Input
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder="Enter username"
                        className="border-[#EDE6D8] rounded-lg"
                        data-testid="middleware-username-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password {middlewareConfig ? '(leave blank to keep current)' : <span className="text-[#EF4444]">*</span>}</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Enter password"
                          className="border-[#EDE6D8] rounded-lg pr-10"
                          data-testid="middleware-password-input"
                          required={!middlewareConfig}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917]"
                        >
                          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-4 bg-[#FCFAF6] rounded-lg">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="middleware-active-switch"
                    />
                    <div>
                      <Label className="cursor-pointer font-medium">Enable automatic token refresh</Label>
                      <p className="text-xs text-[#78716C] mt-1">
                        When enabled, the system will automatically fetch a new token every {formData.refresh_interval_minutes} minutes
                      </p>
                    </div>
                  </div>

                  {/* Current Token Status */}
                  {middlewareConfig?.current_token && (
                    <div className="p-4 bg-[#E6EEE7] border border-[#10B981]/20 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-[#10B981]">Current Token</span>
                        <button
                          onClick={() => setShowToken(!showToken)}
                          className="text-xs text-[#10B981] hover:text-[#1A5F42] flex items-center gap-1"
                        >
                          {showToken ? <EyeSlash size={14} /> : <Eye size={14} />}
                          {showToken ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      <code 
                        className="block text-xs bg-[#FFFFFF] p-2 rounded border border-[#EDE6D8] overflow-x-auto"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {showToken ? middlewareConfig.current_token : '••••••••••••••••••••••••'}
                      </code>
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#78716C]">
                        {middlewareConfig.last_refresh && (
                          <span>Last refresh: {new Date(middlewareConfig.last_refresh).toLocaleString()}</span>
                        )}
                        {middlewareConfig.token_expires_at && (
                          <span>Expires: {new Date(middlewareConfig.token_expires_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {middlewareConfig?.last_error && (
                    <div className="p-4 bg-red-50 border border-[#EF4444]/20 rounded-lg">
                      <span className="text-sm font-medium text-[#EF4444]">Last Error</span>
                      <p className="text-sm text-[#EF4444] mt-1">{middlewareConfig.last_error}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-[#EDE6D8]">
                    <Button
                      onClick={handleSaveConfig}
                      disabled={saving}
                      className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                      data-testid="save-middleware-config-btn"
                    >
                      {saving ? 'Saving...' : 'Save Configuration'}
                    </Button>
                    
                    {middlewareConfig && (
                      <>
                        <Button
                          onClick={handleRefreshToken}
                          disabled={refreshing}
                          variant="outline"
                          className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/10 rounded-lg"
                          data-testid="refresh-token-btn"
                        >
                          <ArrowsClockwise size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                          {refreshing ? 'Refreshing...' : 'Refresh Token Now'}
                        </Button>
                        
                        <Button
                          onClick={handleDeleteConfig}
                          variant="outline"
                          className="border-[#EF4444] text-[#EF4444] hover:bg-red-50 rounded-lg ml-auto"
                          data-testid="delete-middleware-config-btn"
                        >
                          Delete Configuration
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* System tab removed — writes kill-switch still controllable via API */}

        {/* Timezones Tab */}
        <TabsContent value="timezones" className="mt-6">
          <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <Clock size={20} className="text-[#0A2540]" />
              <h2 
                className="text-lg font-semibold text-[#0A2540] tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Available Timezones
              </h2>
            </div>
            <p className="text-sm text-[#78716C] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Timezones available from the Country API. These can be assigned to entities for localized time display.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.countries.filter(c => c.timezone).map((country) => (
                <div 
                  key={country.code}
                  className="flex items-center justify-between p-4 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg hover:border-[#0A2540] transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {country.timezone}
                    </p>
                    <p className="text-xs text-[#78716C] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {country.name} ({country.code})
                    </p>
                  </div>
                  <Clock size={16} className="text-[#78716C]" />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Currencies Tab */}
        <TabsContent value="currencies" className="mt-6">
          <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <CurrencyDollar size={20} className="text-[#0A2540]" />
              <h2 
                className="text-lg font-semibold text-[#0A2540] tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Available Currencies
              </h2>
            </div>
            <p className="text-sm text-[#78716C] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Currencies available from the Country API for financial transactions and display.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.countries.filter(c => c.currency_code).map((country) => (
                <div 
                  key={country.code}
                  className="flex items-center justify-between p-4 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg hover:border-[#0A2540] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A2540] rounded-lg flex items-center justify-center text-white text-xs font-semibold">
                      {country.currency_code}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {country.currency_code}
                      </p>
                      <p className="text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {country.name} ({country.code})
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Countries Tab */}
        <TabsContent value="countries" className="mt-6">
          <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <MapPin size={20} className="text-[#0A2540]" />
              <h2 
                className="text-lg font-semibold text-[#0A2540] tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Supported Countries
              </h2>
            </div>
            <p className="text-sm text-[#78716C] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Countries where EcoWater Cloud operates. Data fetched from middleware API.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {config.countries.map((country) => (
                <CountryCard
                  key={country.code}
                  country={country}
                  rule={resolvePhoneRule(country.code)}
                  isSuperAdmin={isSuperAdmin}
                  onSave={savePhoneRule}
                  onReset={deletePhoneRule}
                />
              ))}
            </div>
          </div>
          {isSuperAdmin && <ScheduledJobsConfig />}
        </TabsContent>

        {/* Subscriber Categories tab removed — backend constant retained for legacy callers */}

        <TabsContent value="courses" className="mt-6" data-testid="courses-tab-content">
          <CoursesConfig />
        </TabsContent>

        <TabsContent value="cx-targets" className="mt-6" data-testid="cx-targets-tab-content">
          <CxTargetsConfig />
        </TabsContent>

        <TabsContent value="cx-templates" className="mt-6" data-testid="cx-templates-tab-content">
          <CxTemplatesConfig />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="emails" className="mt-6" data-testid="emails-tab-content">
            <EmailsConfig />
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="modules" className="mt-6" data-testid="modules-tab-content">
            <ModulesTab />
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="instrumentation" className="mt-6" data-testid="instrumentation-tab-content">
            <InstrumentationConfig />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function KvRow({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-[#EDE6D8] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className="text-xs mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CountryCard — one supported country with an inline phone-rule editor.
// Super-admins get editable Min/Max inputs + Save/Reset. Everyone else sees
// the effective rule as a read-only badge.
// ─────────────────────────────────────────────────────────────────────────
function CountryCard({ country, rule, isSuperAdmin, onSave, onReset }) {
  const [min, setMin] = useState(rule.min);
  const [max, setMax] = useState(rule.max);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMin(rule.min); setMax(rule.max); }, [rule.min, rule.max]);

  const dirty = Number(min) !== Number(rule.min) || Number(max) !== Number(rule.max);
  const invalid = !min || !max || Number(min) < 4 || Number(max) > 20 || Number(max) < Number(min);

  const handleSave = async () => {
    if (invalid) { toast.error('Min must be ≥ 4, Max ≤ 20, and Max ≥ Min'); return; }
    setSaving(true);
    try {
      await onSave(country.code, min, max);
      toast.success(`${country.code} phone rules saved (${min}${Number(min) === Number(max) ? '' : `–${max}`} digits)`);
    } catch (e) {
      toast.error(formatApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await onReset(country.code);
      toast.success(`${country.code} reverted to default rules`);
    } catch (e) {
      toast.error(formatApiError(e, 'Reset failed'));
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid={`country-card-${country.code}`}
      className="p-4 bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg hover:border-[#0A2540] transition-colors flex flex-col gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-[#0A2540] rounded-lg flex items-center justify-center text-white font-semibold">
          {country.code}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-[#1C1917] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {country.name}
          </p>
          {country.mobile_prefix && (
            <p className="text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              +{country.mobile_prefix}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#EDE6D8]">
        {country.currency_code && (
          <div className="text-xs">
            <div className="text-[#78716C]">Currency</div>
            <div className="font-medium text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{country.currency_code}</div>
          </div>
        )}
        {country.timezone && (
          <div className="text-xs">
            <div className="text-[#78716C]">Timezone</div>
            <div className="font-medium text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{country.timezone}</div>
          </div>
        )}
      </div>

      {/* Phone-digit rule editor */}
      <div className="pt-3 border-t border-[#EDE6D8]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Phone digits
          </p>
          {rule.override ? (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0A2540] text-white font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid={`country-rule-source-${country.code}`}
            >
              custom
            </span>
          ) : (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#EDE6D8] text-[#78716C] font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid={`country-rule-source-${country.code}`}
              title={rule.source === 'east_africa_default' ? 'East-Africa default (9 digits)' : 'Global default (7–15 digits)'}
            >
              {rule.source === 'east_africa_default' ? 'EA default' : 'default'}
            </span>
          )}
        </div>

        {isSuperAdmin ? (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-[10px] text-[#78716C] mb-1 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Min</Label>
                <Input
                  type="number"
                  min="4"
                  max="20"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                  className="h-9 rounded-lg border-[#EDE6D8] text-sm"
                  data-testid={`country-min-${country.code}`}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
              </div>
              <div className="pb-2 text-[#A8A29E]">·</div>
              <div className="flex-1">
                <Label className="text-[10px] text-[#78716C] mb-1 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Max</Label>
                <Input
                  type="number"
                  min="4"
                  max="20"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                  className="h-9 rounded-lg border-[#EDE6D8] text-sm"
                  data-testid={`country-max-${country.code}`}
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving || !dirty || invalid}
                className="flex-1 h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg text-xs font-medium disabled:opacity-50"
                data-testid={`country-save-${country.code}`}
              >
                {saving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
              </Button>
              {rule.override && (
                <Button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  variant="outline"
                  className="h-9 rounded-lg text-xs border-[#EDE6D8] text-[#78716C] hover:bg-[#EDE6D8] px-3"
                  data-testid={`country-reset-${country.code}`}
                  title="Revert to default"
                >
                  Reset
                </Button>
              )}
            </div>
            {invalid && (
              <p className="text-[10px] text-[#B94040] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Min ≥ 4 · Max ≤ 20 · Max ≥ Min
              </p>
            )}
          </>
        ) : (
          <div
            className="rounded-lg bg-white border border-[#EDE6D8] px-3 py-2 text-xs"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
            data-testid={`country-rule-readonly-${country.code}`}
          >
            {Number(rule.min) === Number(rule.max) ? `${rule.min} digits` : `${rule.min}–${rule.max} digits`}
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// MODULES TAB (Super-admin only)
// Renders a matrix:  entities (rows)  ×  modules (cols).  Each cell is a
// checkbox that toggles a module for that entity.  `dashboard` is
// permanently locked-on (mandatory) — the checkbox is disabled & checked.
// Changes are tracked locally and committed with the "Save" button per row;
// this keeps the network chatter sane on large rollouts.
// ============================================================================
// Module-key → icon component (phosphor)
const MODULE_ICONS = {
  dashboard:           House,
  subscribers:         UsersIcon,
  cashiers:             UserCircleGear,
  campuses:            GraduationCap,
  stores:              Storefront,
  wallet_transactions: Wallet,
  payments:            CreditCard,
  card_centre:         IdentificationCard,
  analytics:           ChartLineUp,
  users:               UsersIcon,
};

// Frontend fallback metadata — mirrors the backend `MODULE_CATALOGUE`.
// Used when the backend response is missing `description` / `category`
// (e.g. production backend not yet redeployed) so cards never render blanks.
const MODULE_FALLBACK = {
  dashboard:           { description: 'Entity overview, KPIs and analytics',     category: 'General' },
  subscribers:         { description: 'Manage subscribers and card renewals',    category: 'Operations' },
  cashiers:            { description: 'Cashier roster, float & permissions',     category: 'Operations' },
  campuses:            { description: 'Campus directory and configuration',      category: 'Operations' },
  stores:              { description: 'Water store devices and performance',     category: 'Operations' },
  wallet_transactions: { description: 'Subscriber wallet activity ledger',       category: 'Finance' },
  payments:            { description: 'Payment collection and reconciliation',   category: 'Finance' },
  card_centre:         { description: 'Card inventory and lifecycle management', category: 'Operations' },
  analytics:           { description: 'Deep dive reports and trends',            category: 'Insights' },
  users:               { description: 'Manage entity users and roles',           category: 'Administration' },
};

// Category → pastel badge palette
const CATEGORY_TONE = {
  General:        { bg: 'bg-slate-50',   text: 'text-slate-700',   ring: 'ring-slate-200' },
  Operations:     { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  Finance:        { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  Insights:       { bg: 'bg-violet-50',  text: 'text-violet-700',  ring: 'ring-violet-200' },
  Administration: { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },
};

function ModulesTab() {
  const [catalogue, setCatalogue] = useState([]);
  const [rows, setRows] = useState([]);          // [{entity_id, entity_name, country_code, enabled_modules}]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Module-edit modal state
  const [openKey, setOpenKey] = useState(null);  // module key whose modal is open
  const [editSet, setEditSet] = useState(new Set()); // entity_ids selected in the modal
  const [savingModal, setSavingModal] = useState(false);
  // Day Starter popup-groups sub-dialog
  const [popupGroupsOpen, setPopupGroupsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, list] = await Promise.all([
        axios.get(`${API_URL}/api/super-admin/modules/catalogue`, { withCredentials: true }),
        axios.get(`${API_URL}/api/super-admin/entity-modules`, { withCredentials: true }),
      ]);
      // Backfill description/category from the local fallback map so cards
      // render correctly even if the backend response is stale (e.g.
      // production backend not yet redeployed).
      const normalized = (cat.data.modules || []).map(m => {
        const fb = MODULE_FALLBACK[m.key] || {};
        return {
          ...m,
          description: m.description || fb.description || '',
          category:    m.category    || fb.category    || 'General',
        };
      });
      setCatalogue(normalized);
      setRows(list.data || []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load modules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // For a given module key, how many entities currently have it enabled.
  const enabledCount = (modKey) =>
    rows.filter(r => (r.enabled_modules || []).includes(modKey)).length;

  const openModal = (modKey) => {
    const enabledEntities = rows
      .filter(r => (r.enabled_modules || []).includes(modKey))
      .map(r => r.entity_id);
    setEditSet(new Set(enabledEntities));
    setOpenKey(modKey);
  };

  const toggleEntity = (entityId) => {
    setEditSet(prev => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId); else next.add(entityId);
      return next;
    });
  };

  const setAllEntities = (on) => {
    setEditSet(on ? new Set(rows.map(r => r.entity_id)) : new Set());
  };

  const saveModal = async () => {
    if (!openKey) return;
    const mod = catalogue.find(c => c.key === openKey);
    if (!mod) return;
    setSavingModal(true);
    try {
      // For every entity whose membership changed, compute its new enabled list
      // and persist via the existing per-entity endpoint.
      const targets = rows.filter(r => {
        const wasEnabled = (r.enabled_modules || []).includes(openKey);
        const willEnabled = editSet.has(r.entity_id);
        return wasEnabled !== willEnabled;
      });
      if (targets.length === 0) {
        toast.info('No changes to save');
        setOpenKey(null);
        return;
      }
      const updated = await Promise.all(targets.map(async (r) => {
        const current = new Set(r.enabled_modules || []);
        if (editSet.has(r.entity_id)) current.add(openKey);
        else current.delete(openKey);
        // Preserve catalogue order
        const enabled = catalogue.map(c => c.key).filter(k => current.has(k));
        const { data } = await axios.put(
          `${API_URL}/api/super-admin/entity-modules/${r.entity_id}`,
          { enabled_modules: enabled },
          { withCredentials: true },
        );
        return { ...r, enabled_modules: data.enabled_modules };
      }));
      // Merge updated rows back
      setRows(prev => prev.map(r => {
        const u = updated.find(x => x.entity_id === r.entity_id);
        return u || r;
      }));
      toast.success(`"${mod.label}" assignments saved (${targets.length} ${targets.length === 1 ? 'entity' : 'entities'})`);
      setOpenKey(null);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save module assignments'));
    } finally {
      setSavingModal(false);
    }
  };

  const filtered = catalogue.filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (m.label || '').toLowerCase().includes(q)
        || (m.description || '').toLowerCase().includes(q)
        || (m.category || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="bg-white border border-[#EDE6D8] rounded-lg p-12 text-center text-sm text-[#78716C]" data-testid="modules-tab-loading">
        Loading modules…
      </div>
    );
  }

  const activeMod = openKey ? catalogue.find(c => c.key === openKey) : null;
  const ActiveIcon = activeMod ? (MODULE_ICONS[activeMod.key] || Stack) : Stack;
  const modalAllOn = activeMod && rows.length > 0 && rows.every(r => editSet.has(r.entity_id));

  return (
    <div className="space-y-4" data-testid="modules-tab">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Module Management
          </h2>
          <p className="text-xs text-[#78716C] mt-0.5">
            Configure which sidebar modules each entity can access. <span className="font-semibold text-[#0A2540]">Dashboard</span> is mandatory and always on.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search modules by name, description or category…"
          className="pl-9 border-[#EDE6D8] rounded-xl bg-white"
          data-testid="modules-search"
        />
      </div>

      {/* Grid of module cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="modules-grid">
        {filtered.map((m) => {
          const Icon = MODULE_ICONS[m.key] || Stack;
          const tone = CATEGORY_TONE[m.category] || CATEGORY_TONE.General;
          const count = enabledCount(m.key);
          const total = rows.length;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => openModal(m.key)}
              data-testid={`module-card-${m.key}`}
              className="text-left bg-white border border-[#EDE6D8] rounded-2xl p-5 hover:border-[#0A2540]/25 hover:shadow-[0_8px_24px_-12px_rgba(28,25,23,0.15)] transition-all duration-200 flex flex-col gap-3 min-h-[180px]"
            >
              {/* Icon tile */}
              <div className="w-10 h-10 rounded-xl bg-[#F2EBDC] flex items-center justify-center">
                <Icon size={20} className="text-[#0A2540]" weight="duotone" />
              </div>

              {/* Title + description */}
              <div className="flex-1">
                <p className="text-base font-semibold text-[#1C1917] leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {m.label}
                </p>
                <p className="text-xs text-[#78716C] mt-1 leading-snug line-clamp-2">
                  {m.description}
                </p>
              </div>

              {/* Badges */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  {m.mandatory ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-[#0A2540]/10 text-[#0A2540] ring-1 ring-[#0A2540]/15" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <LockKey size={10} weight="fill" /> Core
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-50 text-slate-600 ring-1 ring-slate-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Standard
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ring-1 ${tone.bg} ${tone.text} ${tone.ring}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {m.category}
                  </span>
                </div>
                {!m.mandatory && (
                  <span className="text-[10px] text-[#78716C] font-mono" data-testid={`module-count-${m.key}`}>
                    {count}/{total}
                  </span>
                )}
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-[#EDE6D8] rounded-2xl py-12 text-center text-sm text-[#78716C]">
            No modules match &quot;{search}&quot;.
          </div>
        )}
      </div>

      {/* Per-module assignment modal */}
      <Dialog open={!!openKey} onOpenChange={(o) => { if (!o) setOpenKey(null); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
          {activeMod && (
            <>
              <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#EDE6D8]">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#F2EBDC] flex items-center justify-center">
                    <ActiveIcon size={22} className="text-[#0A2540]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {activeMod.label}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-[#78716C] mt-0.5">
                      {activeMod.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                {/* Module Type */}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[#A8A29E] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    Module Type
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`rounded-xl border p-3 flex flex-col items-center justify-center gap-1 ${activeMod.mandatory ? 'border-[#0A2540] bg-[#0A2540]/5 ring-2 ring-[#0A2540]/15' : 'border-[#EDE6D8] bg-white opacity-60'}`}>
                      <LockKey size={20} className="text-[#0A2540]" weight={activeMod.mandatory ? 'fill' : 'regular'} />
                      <span className="text-xs font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Core</span>
                      <span className="text-[10px] text-[#78716C]">Always enabled</span>
                    </div>
                    <div className={`rounded-xl border p-3 flex flex-col items-center justify-center gap-1 ${!activeMod.mandatory ? 'border-[#0A2540] bg-[#0A2540]/5 ring-2 ring-[#0A2540]/15' : 'border-[#EDE6D8] bg-white opacity-60'}`}>
                      <Stack size={20} className="text-[#0A2540]" weight={!activeMod.mandatory ? 'fill' : 'regular'} />
                      <span className="text-xs font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Standard</span>
                      <span className="text-[10px] text-[#78716C]">Opt-in per entity</span>
                    </div>
                  </div>
                </div>

                {/* Assigned Entities */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Assigned Entities <span className="text-[#0A2540] normal-case">({editSet.size} selected)</span>
                    </p>
                    {!activeMod.mandatory && rows.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setAllEntities(!modalAllOn)}
                        className="text-[11px] font-semibold text-[#0A2540] hover:underline"
                        data-testid="modules-modal-toggle-all"
                      >
                        {modalAllOn ? 'Deselect all' : 'Select all'}
                      </button>
                    )}
                  </div>
                  <div className="rounded-xl border border-[#EDE6D8] divide-y divide-[#EDE6D8] bg-[#FCFAF6]/30 max-h-64 overflow-y-auto">
                    {rows.length === 0 && (
                      <div className="px-4 py-6 text-center text-xs text-[#78716C]">No entities available.</div>
                    )}
                    {rows.map((r) => {
                      const checked = editSet.has(r.entity_id);
                      return (
                        <label
                          key={r.entity_id}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-white ${activeMod.mandatory ? 'opacity-60 cursor-not-allowed' : ''}`}
                          data-testid={`modules-modal-entity-${r.entity_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={activeMod.mandatory ? true : checked}
                            disabled={activeMod.mandatory}
                            onChange={() => toggleEntity(r.entity_id)}
                            className="h-4 w-4 rounded border-[#EDE6D8] text-[#0A2540] focus:ring-[#0A2540] disabled:opacity-60"
                          />
                          <Building size={16} className="text-[#0A2540] shrink-0" weight="duotone" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#1C1917] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                              {r.entity_name}
                            </p>
                            <p className="text-[10px] uppercase tracking-wider text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              ID {r.entity_id}{r.country_code ? ` · ${r.country_code}` : ''}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <DialogFooter className="px-6 py-4 border-t border-[#EDE6D8] bg-white flex-wrap gap-2">
                {activeMod.key === 'day_starter' && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setOpenKey(null); setPopupGroupsOpen(true); }}
                    className="border-[#F5C542] text-[#0A2540] hover:bg-[#F5C542]/10 rounded-lg mr-auto"
                    data-testid="modules-modal-day-starter-groups-btn"
                    disabled={savingModal}
                  >
                    <Sun size={14} weight="fill" className="mr-2 text-[#F5C542]" />
                    Configure
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenKey(null)}
                  className="border-[#EDE6D8] rounded-lg"
                  data-testid="modules-modal-cancel"
                  disabled={savingModal}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={saveModal}
                  disabled={savingModal || activeMod.mandatory}
                  className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                  data-testid="modules-modal-save"
                >
                  <FloppyDisk size={14} className="mr-2" />
                  {savingModal ? 'Saving…' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Day Starter — Popup Groups nested dialog */}
      <Dialog open={popupGroupsOpen} onOpenChange={setPopupGroupsOpen}>
        <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden" data-testid="day-starter-popup-groups-dialog">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#EDE6D8]">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-[#FFF4D6] flex items-center justify-center">
                <Sun size={22} weight="fill" className="text-[#F5C542]" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Day Starter — Popup Groups
                </DialogTitle>
                <DialogDescription className="text-xs text-[#78716C] mt-0.5">
                  Choose which user groups see the first-login-of-the-day analytics popup. The Day Starter module must also be enabled for the entity.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
            <DayStarterConfig bare />
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#EDE6D8] bg-white">
            <Button
              type="button"
              onClick={() => setPopupGroupsOpen(false)}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="day-starter-popup-groups-close"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
